import React from "react";
import { User } from "../../../types";
import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Button from "../../ui/Button";
import StepBack from "../StepBack";
import { calculateCaloriePlan } from "../../../utils/calorieCalculator";

interface SummaryStepProps {
  data: Partial<User>;
  loading: boolean;
  onConfirm: () => void;
  onBack: () => void;
}

const SummaryStep: React.FC<SummaryStepProps> = ({ data, loading, onConfirm, onBack }) => {
  const { t } = useTranslation();
  const caloriePlan = calculateCaloriePlan(data);
  const targetCalories = caloriePlan?.targetCalories ?? 2000;

  const SummaryRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex justify-between items-center py-2.5 border-b border-line last:border-0">
      <span className="text-content-tertiary font-medium text-xs">{label}</span>
      <span className="text-content-primary font-bold text-xs text-right">{value}</span>
    </div>
  );

  return (
    <div className="flex flex-col h-full px-4">
      <StepBack onBack={onBack} />

      <div className="text-center mb-5 mt-2">
        <h2 className="heading-display">{t("wizard.summary.title")}</h2>
        <p className="mt-1 text-sm font-medium text-content-tertiary">
          {t("wizard.summary.subtitle")}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto pb-24 scrollbar-hide">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="rounded-[2rem] border border-line bg-content-primary p-6 text-center text-content-inverse shadow-card mb-4"
        >
          <p className="text-content-inverse/70 font-bold uppercase tracking-widest text-[10px] mb-1">
            {t("wizard.summary.goalLabel")}
          </p>
          <motion.div
            initial={{ scale: 0.7 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", damping: 12, delay: 0.15 }}
            className="text-5xl font-black"
          >
            {targetCalories}
          </motion.div>
          <p className="text-content-inverse/80 font-bold text-xs mt-1">
            {t("wizard.summary.caloriesPerDay")}
          </p>
          {caloriePlan && caloriePlan.goalAdjustment !== 0 && (
            <p className="mt-3 text-xs font-semibold text-content-inverse/75">
              {t("wizard.summary.weeklyEstimate", {
                value: Math.abs(caloriePlan.weeklyWeightChangeKg),
                direction:
                  caloriePlan.goalAdjustment < 0
                    ? t("wizard.summary.loss")
                    : t("wizard.summary.gain"),
              })}
            </p>
          )}
        </motion.div>

        {caloriePlan?.wasClamped && (
          <div className="mb-4 rounded-2xl border border-warning-soft bg-warning-soft px-4 py-3 text-xs font-medium text-warning">
            {t("wizard.summary.clampedWarning", { adjusted: caloriePlan.adjustedTarget })}
          </div>
        )}

        {caloriePlan && (
          <div className="card p-4 mb-4">
            <h3 className="mb-2 text-sm font-black text-content-primary">
              {t("wizard.summary.calculation")}
            </h3>
            <SummaryRow label={t("wizard.summary.bmr")} value={`${caloriePlan.bmr} kcal`} />
            <SummaryRow
              label={t("wizard.summary.activityMultiplier")}
              value={`x${caloriePlan.activityMultiplier}`}
            />
            <SummaryRow label={t("wizard.summary.maintenance")} value={`${caloriePlan.tdee} kcal`} />
            <SummaryRow
              label={t("wizard.summary.goalAdjustment")}
              value={`${caloriePlan.goalAdjustment > 0 ? "+" : ""}${caloriePlan.goalAdjustment} kcal`}
            />
          </div>
        )}

        <div className="card p-4 mb-6">
          <SummaryRow
            label={t("wizard.summary.gender")}
            value={data.gender === "male" ? t("wizard.gender.male") : t("wizard.gender.female")}
          />
          <SummaryRow
            label={t("wizard.summary.details")}
            value={`${data.height} ${t("wizard.bodyStats.heightSuffix")} • ${data.weight} ${t("wizard.bodyStats.weightSuffix")} • ${data.age} ${t("wizard.bodyStats.ageSuffix")}`}
          />
          <SummaryRow
            label={t("wizard.summary.activity")}
            value={data.activityLevel ? t(`wizard.activity.${data.activityLevel}`) : ""}
          />
          <SummaryRow
            label={t("wizard.summary.goalType")}
            value={data.goal ? t(`wizard.goal.${data.goal}`) : ""}
          />
          {data.targetWeight && (
            <SummaryRow
              label={t("wizard.goal.targetWeight")}
              value={`${data.targetWeight} ${t("wizard.bodyStats.weightSuffix")}`}
            />
          )}
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="absolute bottom-0 left-0 w-full px-4 pt-4 pb-[calc(2rem+var(--safe-bottom))] bg-gradient-to-t from-app via-app to-transparent"
      >
        <Button
          variant="primary"
          size="lg"
          fullWidth
          loading={loading}
          icon={CheckCircle2}
          onClick={onConfirm}
        >
          {t("wizard.summary.start")}
        </Button>
      </motion.div>
    </div>
  );
};

export default SummaryStep;
